import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, TrendingUp, Award, Handshake, ArrowLeft, Trophy, Film, Building2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import ScoreBadge from "@/components/ScoreBadge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const PAGE = 30;

export default function Vitrina() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [topCreators, setTopCreators] = useState([]);
  const [topSponsors, setTopSponsors] = useState([]);

  useEffect(() => {
    (async () => {
      const [{ data: h }, { data: c }, { data: s }] = await Promise.all([
        api.get(`/api/highlights?limit=${PAGE}&offset=0`),
        api.get("/api/score/top?role=creator&limit=6").catch(() => ({ data: [] })),
        api.get("/api/score/top?role=sponsor&limit=6").catch(() => ({ data: [] })),
      ]);
      setItems(h.items || []);
      setTotal(h.total || 0);
      setHasMore(!!h.has_more);
      setTopCreators(c || []);
      setTopSponsors(s || []);
    })();
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data: h } = await api.get(`/api/highlights?limit=${PAGE}&offset=${items.length}`);
      setItems((prev) => [...prev, ...(h.items || [])]);
      setHasMore(!!h.has_more);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="marketing-shell min-h-screen">
      <MarketingNav />
      <section className="relative">
        <div className="mx-auto max-w-6xl px-6 pt-12 pb-8">
          <Link to="/" className="text-white/70 inline-flex items-center gap-2 hover:text-gold" data-testid="link-back-home">
            <ArrowLeft size={16} /> {t("common.back")}
          </Link>
          <p className="eyebrow text-gold mt-8">{t("vitrina.eyebrow")}</p>
          <h1 className="font-display text-white text-5xl md:text-6xl tracking-tight mt-4 max-w-3xl leading-tight">
            {t("vitrina.hero_a")} <span className="text-gold">{t("vitrina.hero_b")}</span>.
          </h1>
          <p className="text-white/70 mt-4 max-w-2xl text-lg">
            {t("vitrina.subtitle")}
          </p>
          <div className="mt-6 inline-flex items-center gap-2 glass-card rounded-full px-4 py-2 text-white text-sm" data-testid="vitrina-total-count">
            <TrendingUp size={14} className="text-gold" /> {t("vitrina.milestone_count", { count: total })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5">
        <div className="mx-auto max-w-6xl px-6 py-14">
          {items.length === 0 ? (
            <div className="glass-card rounded-2xl p-10 text-center">
              <Sparkles size={28} className="text-gold mx-auto" />
              <h3 className="font-display text-2xl text-white mt-3">{t("vitrina.empty_title")}</h3>
              <p className="text-white/60 mt-2">{t("vitrina.empty_subtitle")}</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="vitrina-grid">
                {items.map((h) => <HighlightCard key={h.id} h={h} t={t} lang={i18n.resolvedLanguage} />)}
              </div>
              {hasMore && (
                <div className="mt-10 text-center">
                  <Button
                    onClick={loadMore}
                    disabled={loadingMore}
                    data-testid="button-load-more-highlights"
                    variant="outline"
                    className="bg-transparent border-gold/40 text-gold hover:bg-gold/10 hover:text-gold rounded-full px-8 py-6"
                  >
                    {loadingMore ? (
                      <><Loader2 size={14} className="mr-2 animate-spin" /> {t("common.loading")}</>
                    ) : (
                      <>{t("vitrina.load_more", { count: total - items.length })}</>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {(topCreators.length > 0 || topSponsors.length > 0) && (
        <section className="border-t border-white/5">
          <div className="mx-auto max-w-6xl px-6 py-14">
            <div className="flex items-center gap-3 mb-2">
              <Trophy size={20} className="text-gold" />
              <p className="eyebrow text-gold">{t("vitrina.top_profiles_eyebrow")}</p>
            </div>
            <h2 className="font-display text-white text-3xl md:text-4xl mt-2 max-w-2xl leading-tight">
              {t("vitrina.top_profiles_title")}
            </h2>
            <p className="text-white/60 mt-3 max-w-2xl">
              {t("vitrina.top_profiles_subtitle")}
            </p>

            {topCreators.length > 0 && (
              <div className="mt-10" data-testid="top-creators">
                <div className="flex items-center gap-2 mb-4 text-white/80">
                  <Film size={16} className="text-gold" />
                  <h3 className="font-display text-xl">{t("vitrina.top_creators")}</h3>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {topCreators.map((p) => <TopProfileCard key={p.user_id} p={p} />)}
                </div>
              </div>
            )}

            {topSponsors.length > 0 && (
              <div className="mt-12" data-testid="top-sponsors">
                <div className="flex items-center gap-2 mb-4 text-white/80">
                  <Building2 size={16} className="text-gold" />
                  <h3 className="font-display text-xl">{t("vitrina.top_sponsors")}</h3>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {topSponsors.map((p) => <TopProfileCard key={p.user_id} p={p} />)}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <MarketingFooter />
    </div>
  );
}

function TopProfileCard({ p }) {
  const tone =
    p.band === "Élite"
      ? "border-gold/60 shadow-[0_0_40px_-15px_rgba(212,175,55,0.6)]"
      : p.band === "Confiable"
      ? "border-emerald-400/40"
      : "border-white/10";
  return (
    <div className={`glass-card rounded-2xl p-5 border ${tone}`} data-testid={`top-profile-${p.user_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-petroleum to-slate-800 flex items-center justify-center text-gold font-display text-lg shrink-0 overflow-hidden">
            {p.avatar_url ? <img src={p.avatar_url} alt="" className="h-full w-full object-cover" /> : (p.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-white font-display text-lg truncate">{p.name}</p>
            {p.country && <p className="text-white/50 text-xs mt-0.5">{p.country}</p>}
          </div>
        </div>
        <ScoreBadge userId={p.user_id} theme="dark" size="sm" showLabel={false} />
      </div>
      {p.headline && <p className="text-white/60 text-sm mt-3 line-clamp-3">{p.headline}</p>}
    </div>
  );
}

function HighlightCard({ h, t, lang }) {
  const iconMap = {
    milestone_paid: TrendingUp,
    agreement_signed: Handshake,
  };
  const Icon = iconMap[h.kind] || Award;
  const label = h.kind === "milestone_paid" ? t("vitrina.kind_milestone_paid") : h.kind === "agreement_signed" ? t("vitrina.kind_agreement_signed") : t("vitrina.kind_milestone_paid");
  return (
    <div
      className="glass-card rounded-2xl p-6 hover:border-gold/40 transition-colors"
      data-testid={`highlight-${h.id}`}
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold/15 text-gold">
          <Icon size={18} />
        </span>
        <div>
          <p className="eyebrow text-gold text-[10px]">{label}</p>
          <p className="text-white/60 text-xs mt-0.5">
            {new Date(h.created_at).toLocaleDateString(lang || "es", { day: "2-digit", month: "long", year: "numeric" })}
          </p>
        </div>
      </div>
      <p className="text-white text-lg mt-4 font-display leading-snug">{h.text}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {h.genre && <span className="text-[10px] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/70 uppercase tracking-widest">{h.genre}</span>}
        {h.country && <span className="text-[10px] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/70 uppercase tracking-widest">{h.country}</span>}
        {h.modality && <span className="text-[10px] px-2 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold uppercase tracking-widest">{h.modality}</span>}
      </div>
    </div>
  );
}
